import {
  COGO_CANDIDATE_LIFECYCLE_EVENT_SCHEMA_VERSION,
  assertEvidenceCapsuleRef,
  candidateLifecycleJournalData,
  type CandidateLifecycleActorAuthority,
  type CandidateLifecycleEvidenceBinding,
  type CandidateLifecycleProjection,
  type CandidateLifecycleState,
  type CandidateLifecycleTransition,
  type EvidenceCapsuleRef,
} from '@cogo/schema/productization-spine'
import {
  COOGO_EXECUTION_EVENT_VERSION,
  COOGO_ORCHESTRATION_PLAN_VERSION,
  validateCoogoExecutionEvent,
  validateCoogoOrchestrationPlan,
  type CoogoExecutionEvent,
  type CoogoOrchestrationPlan,
} from '@cogo/webmcp-runtime'
import {
  appendStudioCandidateLifecycleJournalEvent,
  appendStudioOrchestrationJournalEvent,
  createStudioOrchestrationJournal,
  deriveStudioCandidateLifecycleProjections,
  readStudioOrchestrationJournal,
  type StudioOrchestrationJournalMode,
  type StudioOrchestrationRunReadback,
} from './studio-orchestration-journal-api'
import type {
  StudioCandidateDemandSignalEvidence,
  StudioCandidatePreviewPlanEvidence,
} from '@/features/productization/candidate-lifecycle-entry-evidence'
import {
  readProjectCandidateLifecycleDescriptor,
  type StudioCandidateLifecycleDescriptor,
} from '@/features/productization/candidate-verification-model'
import { loadProject } from '@/services/project-api'

export interface CandidateGenerationLifecycleInput {
  projectId: string
  skuId: string
  candidateId?: string
  sourceAssessmentRevision: string | null
  demandSignalRef: EvidenceCapsuleRef
  previewPlanRef: EvidenceCapsuleRef
  entryEvidence?: {
    demandSignal: StudioCandidateDemandSignalEvidence
    previewPlan: StudioCandidatePreviewPlanEvidence
  }
  beforeCreateJournal?: (
    locator: CandidateGenerationLifecycleLocator,
  ) => Promise<void>
  beforeRecordDemand?: (
    locator: CandidateGenerationLifecycleLocator,
  ) => Promise<void>
  signal?: AbortSignal
}

export interface CandidateGenerationLifecycleLocator {
  projectId: string
  skuId: string
  candidateId: string
  journalRunId: string
  candidateRevision: string
  assessmentRevision: string | null
}

export interface CandidateGenerationVerificationInput {
  sufficiencyAssessmentRef: EvidenceCapsuleRef
  validatorSafetyPassRef: EvidenceCapsuleRef
  secondModelJudgeRef: EvidenceCapsuleRef
  renderExecutionReceiptRef: EvidenceCapsuleRef
  verificationCapsuleRef: EvidenceCapsuleRef
}

export interface CandidateGenerationLifecycleController {
  projectId: string
  skuId: string
  candidateId: string
  journalRunId: string
  candidateRevision: string
  assessmentRevision: string | null
  terminate: (reasonCode?: string) => Promise<void>
  markPreview: () => Promise<CandidateLifecycleProjection>
  verify: (
    input: CandidateGenerationVerificationInput,
  ) => Promise<CandidateLifecycleProjection>
}

export interface CandidateGenerationLifecycleDependencies {
  createJournal: (
    plan: CoogoOrchestrationPlan,
  ) => Promise<StudioOrchestrationJournalMode>
  appendEvent: (
    event: CoogoExecutionEvent,
  ) => Promise<StudioOrchestrationJournalMode>
  appendJournalEvent: (
    event: CoogoExecutionEvent,
  ) => Promise<StudioOrchestrationJournalMode>
  readJournal: (input: {
    projectId: string
    runId: string
  }) => Promise<StudioOrchestrationRunReadback | null>
  nextId: () => string
  now: () => string
}

export interface ResumeCandidateGenerationPreviewDependencies
  extends CandidateGenerationLifecycleDependencies {
  readPersistedDescriptor: (
    projectId: string,
  ) => Promise<StudioCandidateLifecycleDescriptor | null>
}

export class CandidateLifecycleTerminationError extends Error {
  override readonly name = 'CandidateLifecycleTerminationError'
}

export class CandidateLifecycleAuthorityUncertainError extends Error {
  override readonly name = 'CandidateLifecycleAuthorityUncertainError'
  readonly locator: CandidateGenerationLifecycleLocator

  constructor(
    message: string,
    locator: CandidateGenerationLifecycleLocator,
    options?: ErrorOptions,
  ) {
    super(message, options)
    this.locator = { ...locator }
  }
}

const DEFAULT_DEPENDENCIES: CandidateGenerationLifecycleDependencies = {
  createJournal: createStudioOrchestrationJournal,
  appendEvent: appendStudioCandidateLifecycleJournalEvent,
  appendJournalEvent: appendStudioOrchestrationJournalEvent,
  readJournal: readStudioOrchestrationJournal,
  nextId: () => crypto.randomUUID(),
  now: () => new Date().toISOString(),
}

const DEFAULT_RESUME_DEPENDENCIES: ResumeCandidateGenerationPreviewDependencies = {
  ...DEFAULT_DEPENDENCIES,
  readPersistedDescriptor: async (projectId) => {
    const persisted = await loadProject(projectId)
    return readProjectCandidateLifecycleDescriptor({
      settings: persisted.settings,
      metadata: (persisted.app as unknown as Record<string, unknown>).metadata,
    })
  },
}

const RECORD_DEMAND_NODE_ID = 'candidate-record-demand'
const PLAN_NODE_ID = 'candidate-plan-preview'
const VERIFY_NODE_ID = 'candidate-verify-proof'
const DEFAULT_TERMINATION_REASON = 'generation-request-aborted'

interface LifecycleScope {
  projectId: string
  skuId: string
  candidateId: string
  plan: CoogoOrchestrationPlan
}

function requiredText(value: string, label: string): string {
  const normalized = value.trim()
  if (!normalized) throw new Error(`${label} is required.`)
  return normalized
}

function cloneEvidenceRef(value: EvidenceCapsuleRef): EvidenceCapsuleRef {
  assertEvidenceCapsuleRef(value)
  return { ref: value.ref, contentHash: value.contentHash }
}

function buildLifecyclePlan(input: {
  projectId: string
  skuId: string
  candidateId: string
  planId: string
  runId: string
  createdAt: string
  assessmentRevision: string | null
  demandSignalRef: EvidenceCapsuleRef
  previewPlanRef: EvidenceCapsuleRef
  entryEvidence?: CandidateGenerationLifecycleInput['entryEvidence']
}): CoogoOrchestrationPlan {
  const plan: CoogoOrchestrationPlan = {
    schemaVersion: COOGO_ORCHESTRATION_PLAN_VERSION,
    planId: input.planId,
    label: `Candidate lifecycle for ${input.skuId}`,
    runId: input.runId,
    projectId: input.projectId,
    trigger: 'studio-ui',
    createdAt: input.createdAt,
    nodes: [
      {
        id: RECORD_DEMAND_NODE_ID,
        kind: 'tool',
        label: 'Record verified demand signal',
        operation: {
          name: 'cogo.productization.record-demand',
          params: {
            skuId: input.skuId,
            candidateId: input.candidateId,
            evidenceRef: cloneEvidenceRef(input.demandSignalRef),
            ...(input.entryEvidence
              ? { evidenceArtifact: input.entryEvidence.demandSignal }
              : {}),
          },
        },
        policy: {
          authority: 'studio',
          readOnly: false,
          confirmation: 'policy',
          retry: 'never',
          timeoutMs: 15_000,
          idempotencyKey: `${input.runId}:${RECORD_DEMAND_NODE_ID}`,
        },
      },
      {
        id: PLAN_NODE_ID,
        kind: 'tool',
        label: 'Admit generated preview candidate',
        dependsOn: [RECORD_DEMAND_NODE_ID],
        operation: {
          name: 'cogo.productization.plan',
          params: {
            skuId: input.skuId,
            candidateId: input.candidateId,
            evidenceRef: cloneEvidenceRef(input.previewPlanRef),
            ...(input.entryEvidence
              ? { evidenceArtifact: input.entryEvidence.previewPlan }
              : {}),
          },
        },
        policy: {
          authority: 'studio',
          readOnly: false,
          confirmation: 'policy',
          retry: 'never',
          timeoutMs: 15_000,
          idempotencyKey: `${input.runId}:${PLAN_NODE_ID}`,
        },
      },
      {
        id: VERIFY_NODE_ID,
        kind: 'gate',
        label: 'Verify candidate evidence capsule',
        dependsOn: [PLAN_NODE_ID],
        operation: {
          name: 'cogo.productization.verify',
          params: {
            skuId: input.skuId,
            candidateId: input.candidateId,
            assessmentRevision: input.assessmentRevision,
          },
        },
        policy: {
          authority: 'runtime',
          readOnly: true,
          confirmation: 'policy',
          retry: 'never',
          timeoutMs: 15_000,
        },
      },
    ],
  }
  const validation = validateCoogoOrchestrationPlan(plan)
  if (!validation.valid) {
    throw new Error(
      `Invalid candidate lifecycle orchestration plan: ${validation.errors.join('; ')}`,
    )
  }
  return plan
}

function projectionFor(
  readback: StudioOrchestrationRunReadback,
  scope: LifecycleScope,
): CandidateLifecycleProjection | null {
  return (
    deriveStudioCandidateLifecycleProjections(readback.events).find(
      (projection) =>
        projection.projectId === scope.projectId &&
        projection.journalRunId === scope.plan.runId &&
        projection.skuId === scope.skuId &&
        projection.candidateId === scope.candidateId,
    ) ?? null
  )
}

function executionNodeState(
  readback: StudioOrchestrationRunReadback,
  nodeId: string,
): 'pending' | 'running' | 'completed' | 'failed' {
  let state: 'pending' | 'running' | 'completed' | 'failed' = 'pending'
  for (const event of readback.events) {
    if (event.nodeId !== nodeId) continue
    if (event.type === 'node.started') state = 'running'
    if (event.type === 'node.completed') state = 'completed'
    if (event.type === 'node.failed') state = 'failed'
  }
  return state
}

function evidenceBindingsEqual(
  left: readonly CandidateLifecycleEvidenceBinding[],
  right: readonly CandidateLifecycleEvidenceBinding[],
): boolean {
  return (
    left.length === right.length &&
    left.every(
      (binding, index) =>
        binding.role === right[index]?.role &&
        binding.ref.ref === right[index]?.ref.ref &&
        binding.ref.contentHash === right[index]?.ref.contentHash,
    )
  )
}

function exactPreviewPlanRef(
  readback: StudioOrchestrationRunReadback,
): EvidenceCapsuleRef {
  const planNode = readback.plan.nodes.find(
    (node) => node.id === PLAN_NODE_ID,
  )
  const operation = planNode?.operation
  if (
    !operation ||
    operation.name !== 'cogo.productization.plan' ||
    !operation.params ||
    typeof operation.params !== 'object' ||
    Array.isArray(operation.params)
  ) {
    throw new Error('Candidate preview lifecycle plan binding is invalid.')
  }
  const evidenceRef = operation.params.evidenceRef
  assertEvidenceCapsuleRef(evidenceRef)
  return cloneEvidenceRef(evidenceRef)
}

function acknowledgedPreviewProjection(
  readback: StudioOrchestrationRunReadback,
  scope: LifecycleScope,
  previewPlanRef: EvidenceCapsuleRef,
): CandidateLifecycleProjection | null {
  if (executionNodeState(readback, PLAN_NODE_ID) !== 'completed') return null
  const projection = projectionFor(readback, scope)
  const previewTransition = projection?.history.find(
    (event) => event.transition === 'plan' && event.toState === 'preview-candidate',
  )
  if (!projection || !previewTransition) {
    throw new Error('Candidate preview lifecycle readback is inconsistent.')
  }
  if (
    !evidenceBindingsEqual(previewTransition.evidenceRefs, [
      { role: 'preview-plan', ref: previewPlanRef },
    ])
  ) {
    throw new Error(
      'Candidate preview lifecycle does not match the persisted preview plan.',
    )
  }
  return projection
}

async function requireReadback(
  scope: LifecycleScope,
  dependencies: CandidateGenerationLifecycleDependencies,
): Promise<StudioOrchestrationRunReadback> {
  const readback = await dependencies.readJournal({
    projectId: scope.projectId,
    runId: scope.plan.runId,
  })
  if (!readback) {
    throw new Error(
      'Candidate lifecycle requires an authenticated durable journal readback.',
    )
  }
  if (
    readback.run.projectId !== scope.projectId ||
    readback.run.runId !== scope.plan.runId ||
    readback.run.planId !== scope.plan.planId
  ) {
    throw new Error('Candidate lifecycle journal readback scope is invalid.')
  }
  return readback
}

async function appendAndAcknowledge(input: {
  scope: LifecycleScope
  dependencies: CandidateGenerationLifecycleDependencies
  type: CoogoExecutionEvent['type']
  nodeId?: string
  data?: Record<string, unknown>
  readback?: StudioOrchestrationRunReadback
  eventId?: string
  occurredAt?: string
}): Promise<{
  event: CoogoExecutionEvent
  readback: StudioOrchestrationRunReadback
}> {
  const before =
    input.readback ?? (await requireReadback(input.scope, input.dependencies))
  const event: CoogoExecutionEvent = {
    schemaVersion: COOGO_EXECUTION_EVENT_VERSION,
    eventId: input.eventId ?? input.dependencies.nextId(),
    planId: input.scope.plan.planId,
    runId: input.scope.plan.runId,
    projectId: input.scope.projectId,
    sequence: before.run.lastSequence + 1,
    occurredAt: input.occurredAt ?? input.dependencies.now(),
    type: input.type,
    ...(input.nodeId ? { nodeId: input.nodeId } : {}),
    ...(input.data ? { data: input.data } : {}),
  }
  const validation = validateCoogoExecutionEvent(event)
  if (!validation.valid) {
    throw new Error(
      `Invalid candidate lifecycle journal event: ${validation.errors.join('; ')}`,
    )
  }
  const mode = await input.dependencies.appendJournalEvent(event)
  if (mode !== 'durable') {
    throw new Error('Candidate lifecycle transitions require durable storage.')
  }

  const acknowledged = await requireReadback(input.scope, input.dependencies)
  const persisted = acknowledged.events.find(
    (candidate) => candidate.sequence === event.sequence,
  )
  if (
    acknowledged.run.lastSequence !== event.sequence ||
    persisted?.eventId !== event.eventId ||
    persisted.type !== event.type ||
    persisted.nodeId !== event.nodeId
  ) {
    throw new Error('Candidate lifecycle journal event was not acknowledged.')
  }
  return { event, readback: acknowledged }
}

async function terminateLifecyclePlan(input: {
  scope: LifecycleScope
  dependencies: CandidateGenerationLifecycleDependencies
  reasonCode: string
}): Promise<void> {
  const readback = await requireReadback(input.scope, input.dependencies)
  if (readback.run.status === 'failed' || readback.run.status === 'completed') {
    return
  }
  await appendAndAcknowledge({
    scope: input.scope,
    dependencies: input.dependencies,
    type: 'plan.failed',
    data: { reasonCode: requiredText(input.reasonCode, 'reasonCode') },
    readback,
  })
}

function generationAbortError(): Error {
  const error = new Error('Generation request was cancelled.')
  error.name = 'AbortError'
  return error
}

async function appendTransition(input: {
  scope: LifecycleScope
  dependencies: CandidateGenerationLifecycleDependencies
  nodeId: string
  transition: CandidateLifecycleTransition
  toState: CandidateLifecycleState
  actorAuthority: CandidateLifecycleActorAuthority
  actorRef: string
  evidenceRefs: CandidateLifecycleEvidenceBinding[]
  reasonCode: string
}): Promise<CandidateLifecycleProjection> {
  const readback = await requireReadback(input.scope, input.dependencies)
  const current = projectionFor(readback, input.scope)
  const nodeState = executionNodeState(readback, input.nodeId)
  if (nodeState === 'completed' || nodeState === 'failed') {
    throw new Error(`Candidate lifecycle node is already ${nodeState}.`)
  }
  const eventId = input.dependencies.nextId()
  const occurredAt = input.dependencies.now()
  const candidateEvent = {
    schemaVersion: COGO_CANDIDATE_LIFECYCLE_EVENT_SCHEMA_VERSION,
    transitionId: eventId,
    journalRunId: input.scope.plan.runId,
    aggregateVersion: (current?.aggregateVersion ?? 0) + 1,
    previousTransitionId: current?.lastTransitionId ?? null,
    skuId: input.scope.skuId,
    candidateId: input.scope.candidateId,
    projectId: input.scope.projectId,
    fromState: current?.state ?? null,
    toState: input.toState,
    transition: input.transition,
    occurredAt,
    actor: {
      authority: input.actorAuthority,
      actorRef: input.actorRef,
    },
    evidenceRefs: input.evidenceRefs.map((binding) => ({
      role: binding.role,
      ref: cloneEvidenceRef(binding.ref),
    })),
    reasonCode: input.reasonCode,
  } as const
  const journalData = candidateLifecycleJournalData(candidateEvent)
  const completionBase =
    nodeState === 'running'
      ? readback
      : (
          await appendAndAcknowledge({
            scope: input.scope,
            dependencies: input.dependencies,
            type: 'node.started',
            nodeId: input.nodeId,
            readback,
          })
        ).readback
  const event: CoogoExecutionEvent = {
    schemaVersion: COOGO_EXECUTION_EVENT_VERSION,
    eventId,
    planId: input.scope.plan.planId,
    runId: input.scope.plan.runId,
    projectId: input.scope.projectId,
    sequence: completionBase.run.lastSequence + 1,
    occurredAt,
    type: 'node.completed',
    nodeId: input.nodeId,
    data: journalData,
  }
  const eventValidation = validateCoogoExecutionEvent(event)
  if (!eventValidation.valid) {
    throw new Error(
      `Invalid candidate lifecycle journal event: ${eventValidation.errors.join('; ')}`,
    )
  }
  const mode = await input.dependencies.appendEvent(event)
  if (mode !== 'durable') {
    throw new Error('Candidate lifecycle transitions require durable storage.')
  }

  const acknowledged = await requireReadback(input.scope, input.dependencies)
  const projection = projectionFor(acknowledged, input.scope)
  if (
    !projection ||
    projection.lastTransitionId !== eventId ||
    projection.state !== input.toState
  ) {
    throw new Error(
      'Candidate lifecycle transition readback was not acknowledged.',
    )
  }
  return projection
}

async function acceptLifecyclePlan(input: {
  scope: LifecycleScope
  dependencies: CandidateGenerationLifecycleDependencies
}): Promise<void> {
  const event: CoogoExecutionEvent = {
    schemaVersion: COOGO_EXECUTION_EVENT_VERSION,
    eventId: input.dependencies.nextId(),
    planId: input.scope.plan.planId,
    runId: input.scope.plan.runId,
    projectId: input.scope.projectId,
    sequence: 0,
    occurredAt: input.dependencies.now(),
    type: 'plan.accepted',
  }
  const validation = validateCoogoExecutionEvent(event)
  if (!validation.valid) {
    throw new Error(
      `Invalid candidate lifecycle plan acceptance event: ${validation.errors.join('; ')}`,
    )
  }
  const mode = await input.dependencies.appendJournalEvent(event)
  if (mode !== 'durable') {
    throw new Error(
      'Candidate lifecycle plan acceptance requires durable storage.',
    )
  }

  const acknowledged = await requireReadback(input.scope, input.dependencies)
  const acceptedEvent = acknowledged.events.find(
    (candidate) => candidate.sequence === event.sequence,
  )
  if (
    acknowledged.run.lastSequence !== event.sequence ||
    acceptedEvent?.eventId !== event.eventId ||
    acceptedEvent.type !== event.type ||
    acceptedEvent.planId !== event.planId ||
    acceptedEvent.runId !== event.runId ||
    acceptedEvent.projectId !== event.projectId
  ) {
    throw new Error(
      'Candidate lifecycle plan acceptance readback was not acknowledged.',
    )
  }
}

/**
 * Re-enters the durable candidate lifecycle after a browser refresh. The
 * descriptor is read back from the persisted project rather than accepted
 * from the reconnect card, and the journal remains the only state authority.
 * A completed preview transition is returned idempotently; a pending one is
 * advanced with the same sequence/aggregate CAS and exact readback used by the
 * original in-session controller.
 */
export async function resumePersistedCandidateGenerationPreview(
  projectIdInput: string,
  dependencies: ResumeCandidateGenerationPreviewDependencies =
    DEFAULT_RESUME_DEPENDENCIES,
): Promise<CandidateLifecycleProjection | null> {
  const projectId = requiredText(projectIdInput, 'projectId')
  const descriptor = await dependencies.readPersistedDescriptor(projectId)
  if (!descriptor) return null
  if (descriptor.projectId !== projectId) {
    throw new Error('Candidate lifecycle persisted project scope is invalid.')
  }
  const readback = await dependencies.readJournal({
    projectId,
    runId: descriptor.journalRunId,
  })
  if (!readback) {
    throw new Error(
      'Candidate lifecycle requires an authenticated durable journal readback.',
    )
  }
  if (
    readback.run.projectId !== projectId ||
    readback.run.runId !== descriptor.journalRunId ||
    readback.plan.projectId !== projectId ||
    readback.plan.runId !== descriptor.journalRunId ||
    readback.plan.planId !== readback.run.planId
  ) {
    throw new Error('Candidate lifecycle journal readback scope is invalid.')
  }
  const scope: LifecycleScope = {
    projectId,
    skuId: descriptor.skuId,
    candidateId: descriptor.candidateId,
    plan: readback.plan,
  }
  const previewPlanRef = exactPreviewPlanRef(readback)
  if (previewPlanRef.contentHash !== descriptor.candidateRevision) {
    throw new Error(
      'Candidate lifecycle persisted revision does not match the durable preview plan.',
    )
  }
  const acknowledged = acknowledgedPreviewProjection(
    readback,
    scope,
    previewPlanRef,
  )
  if (acknowledged) return acknowledged

  const current = projectionFor(readback, scope)
  if (!current || current.state !== 'demand-candidate') {
    throw new Error(
      'Candidate lifecycle is not eligible for a preview transition.',
    )
  }
  try {
    return await appendTransition({
      scope,
      dependencies,
      nodeId: PLAN_NODE_ID,
      transition: 'plan',
      toState: 'preview-candidate',
      actorAuthority: 'generation',
      actorRef: 'generation:cogo.studio.requestAppGeneration',
      evidenceRefs: [{ role: 'preview-plan', ref: previewPlanRef }],
      reasonCode: 'generation-preview-ready',
    })
  } catch (cause) {
    // A concurrent reconnect may win the append CAS after our initial
    // readback. Accept only the exact persisted transition; otherwise preserve
    // the original failure for a visible, retryable reconnect error.
    const concurrent = await requireReadback(scope, dependencies)
    const concurrentProjection = acknowledgedPreviewProjection(
      concurrent,
      scope,
      previewPlanRef,
    )
    if (concurrentProjection) return concurrentProjection
    throw cause
  }
}

export async function createCandidateGenerationLifecycle(
  input: CandidateGenerationLifecycleInput,
  dependencies: CandidateGenerationLifecycleDependencies = DEFAULT_DEPENDENCIES,
): Promise<CandidateGenerationLifecycleController> {
  const projectId = requiredText(input.projectId, 'projectId')
  const skuId = requiredText(input.skuId, 'skuId')
  const candidateId = requiredText(
    input.candidateId ?? `candidate-${dependencies.nextId()}`,
    'candidateId',
  )
  const assessmentRevision = input.sourceAssessmentRevision
  const demandSignalRef = cloneEvidenceRef(input.demandSignalRef)
  const previewPlanRef = cloneEvidenceRef(input.previewPlanRef)
  const candidateRevision = previewPlanRef.contentHash
  const runId = dependencies.nextId()
  const plan = buildLifecyclePlan({
    projectId,
    skuId,
    candidateId,
    planId: dependencies.nextId(),
    runId,
    createdAt: dependencies.now(),
    demandSignalRef,
    previewPlanRef,
    assessmentRevision,
    entryEvidence: input.entryEvidence,
  })
  const locator: CandidateGenerationLifecycleLocator = {
    projectId,
    skuId,
    candidateId,
    journalRunId: plan.runId,
    candidateRevision,
    assessmentRevision,
  }
  if (input.signal?.aborted) throw generationAbortError()
  await input.beforeCreateJournal?.(locator)
  if (input.signal?.aborted) throw generationAbortError()
  let mode: StudioOrchestrationJournalMode
  try {
    mode = await dependencies.createJournal(plan)
  } catch (cause) {
    throw new CandidateLifecycleAuthorityUncertainError(
      `Candidate lifecycle journal creation has an uncertain durable outcome: ${cause instanceof Error ? cause.message : String(cause)}`,
      locator,
      { cause },
    )
  }
  if (mode !== 'durable') {
    throw new Error(
      'Candidate lifecycle requires an authenticated durable journal.',
    )
  }

  const scope: LifecycleScope = { projectId, skuId, candidateId, plan }
  let failureReasonCode = 'candidate-lifecycle-initialization-failed'
  let planAccepted = false
  try {
    try {
      await acceptLifecyclePlan({ scope, dependencies })
    } catch (cause) {
      throw new CandidateLifecycleAuthorityUncertainError(
        `Candidate lifecycle plan acceptance has an uncertain durable outcome: ${cause instanceof Error ? cause.message : String(cause)}`,
        locator,
        { cause },
      )
    }
    planAccepted = true
    if (input.signal?.aborted) throw generationAbortError()
    if (input.beforeRecordDemand) {
      failureReasonCode = 'candidate-lifecycle-locator-persistence-failed'
      await input.beforeRecordDemand({
        projectId,
        skuId,
        candidateId,
        journalRunId: plan.runId,
        candidateRevision,
        assessmentRevision,
      })
      failureReasonCode = 'candidate-lifecycle-initialization-failed'
    }
    if (input.signal?.aborted) throw generationAbortError()
    failureReasonCode = 'candidate-lifecycle-demand-recording-failed'
    await appendTransition({
      scope,
      dependencies,
      nodeId: RECORD_DEMAND_NODE_ID,
      transition: 'record-demand',
      toState: 'demand-candidate',
      actorAuthority: 'studio',
      actorRef: 'studio:generation-entry',
      evidenceRefs: [{ role: 'demand-signal', ref: demandSignalRef }],
      reasonCode: 'user-confirmed-generation-demand',
    })
    if (input.signal?.aborted) throw generationAbortError()
  } catch (cause) {
    const reasonCode =
      input.signal?.aborted ||
      (cause instanceof Error && cause.name === 'AbortError')
        ? DEFAULT_TERMINATION_REASON
        : failureReasonCode
    if (planAccepted) {
      try {
        await terminateLifecyclePlan({ scope, dependencies, reasonCode })
      } catch (terminationCause) {
        throw new CandidateLifecycleTerminationError(
          `Candidate lifecycle initialization failed and the durable plan could not be terminated: ${terminationCause instanceof Error ? terminationCause.message : String(terminationCause)}`,
          { cause },
        )
      }
    }
    throw cause instanceof Error
      ? cause
      : new Error('Candidate lifecycle initialization failed.')
  }

  return {
    projectId,
    skuId,
    candidateId,
    journalRunId: plan.runId,
    candidateRevision,
    assessmentRevision,
    terminate: (reasonCode = DEFAULT_TERMINATION_REASON) =>
      terminateLifecyclePlan({ scope, dependencies, reasonCode }),
    markPreview: () =>
      appendTransition({
        scope,
        dependencies,
        nodeId: PLAN_NODE_ID,
        transition: 'plan',
        toState: 'preview-candidate',
        actorAuthority: 'generation',
        actorRef: 'generation:cogo.studio.requestAppGeneration',
        evidenceRefs: [{ role: 'preview-plan', ref: previewPlanRef }],
        reasonCode: 'generation-preview-ready',
      }),
    verify: async (verificationInput) => {
      if (!assessmentRevision) {
        throw new Error(
          'Candidate verification requires an immutable source assessment revision.',
        )
      }
      const evidenceRefs: CandidateLifecycleEvidenceBinding[] = [
        {
          role: 'sufficiency-assessment',
          ref: cloneEvidenceRef(verificationInput.sufficiencyAssessmentRef),
        },
        {
          role: 'validator-safety-pass',
          ref: cloneEvidenceRef(verificationInput.validatorSafetyPassRef),
        },
        {
          role: 'second-model-judge',
          ref: cloneEvidenceRef(verificationInput.secondModelJudgeRef),
        },
        {
          role: 'render-execution-receipt',
          ref: cloneEvidenceRef(verificationInput.renderExecutionReceiptRef),
        },
        {
          role: 'verification-capsule',
          ref: cloneEvidenceRef(verificationInput.verificationCapsuleRef),
        },
      ]
      if (evidenceRefs[0].ref.contentHash !== assessmentRevision) {
        throw new Error(
          'Candidate verification evidence does not match the source assessment revision.',
        )
      }
      const before = await requireReadback(scope, dependencies)
      const existing = projectionFor(before, scope)
      const verifyNodeState = executionNodeState(before, VERIFY_NODE_ID)
      const alreadyVerified =
        existing?.state === 'verified' && verifyNodeState === 'completed'
      if (
        alreadyVerified &&
        !evidenceBindingsEqual(
          existing.history.at(-1)?.evidenceRefs ?? [],
          evidenceRefs,
        )
      ) {
        throw new Error(
          'Candidate verification retry does not match the committed evidence.',
        )
      }
      const projection = alreadyVerified
        ? existing
        : await appendTransition({
            scope,
            dependencies,
            nodeId: VERIFY_NODE_ID,
            transition: 'verify',
            toState: 'verified',
            actorAuthority: 'runtime',
            actorRef: 'runtime:productization-verification-gate',
            evidenceRefs,
            reasonCode: 'validator-second-model-render-evidence-passed',
          })
      const completionReadback = await requireReadback(scope, dependencies)
      if (completionReadback.run.status !== 'completed') {
        await appendAndAcknowledge({
          scope,
          dependencies,
          type: 'plan.completed',
          readback: completionReadback,
        })
      }
      return projection
    },
  }
}
